// ══════════════════════════════════════════════════════════════
//  PCEA THINGATI CMS — Asset Depreciation Engine
//  Supports: Straight Line Method (SLM) & Reducing Balance (RBM)
// ══════════════════════════════════════════════════════════════

/**
 * Calculate years elapsed since purchase date
 */
export function yearsElapsed(purchaseDateStr) {
  if (!purchaseDateStr) return 0;
  const purchase = new Date(purchaseDateStr);
  const now      = new Date();
  const ms       = now - purchase;
  return Math.max(0, ms / (365.25 * 24 * 60 * 60 * 1000));
}

/**
 * STRAIGHT LINE METHOD
 * Asset loses the same fixed amount every year.
 * Formula: Annual Dep = (Cost - Salvage) / Useful Life
 *          Book Value = Cost - (Annual Dep × Years Used)
 *
 * @param {number} cost          - Original purchase price (KES)
 * @param {number} salvageValue  - Residual value at end of life (KES), default 0
 * @param {number} usefulLife    - Total useful life in years
 * @param {number} yearsUsed     - How many years the asset has been in use
 * @returns {object}             - Depreciation details
 */
export function straightLine(cost, salvageValue = 0, usefulLife = 5, yearsUsed = 0) {
  if (!cost || cost <= 0) return emptyResult(cost);

  const depreciableAmount = cost - salvageValue;
  const annualDep         = usefulLife > 0 ? depreciableAmount / usefulLife : 0;
  const totalDep          = Math.min(annualDep * yearsUsed, depreciableAmount);
  const bookValue         = Math.max(cost - totalDep, salvageValue);
  const pctDepreciated    = cost > 0 ? Math.min((totalDep / cost) * 100, 100) : 0;
  const yearsRemaining    = Math.max(0, usefulLife - yearsUsed);
  const isFullyDep        = bookValue <= salvageValue || yearsUsed >= usefulLife;

  return {
    method:          'Straight Line',
    cost,
    annualDep:       Math.round(annualDep),
    totalDep:        Math.round(totalDep),
    bookValue:       Math.round(bookValue),
    salvageValue:    Math.round(salvageValue),
    pctDepreciated:  Math.round(pctDepreciated),
    yearsUsed:       Math.round(yearsUsed * 10) / 10,
    yearsRemaining:  Math.round(yearsRemaining * 10) / 10,
    usefulLife,
    isFullyDep,
  };
}

/**
 * REDUCING BALANCE METHOD (Diminishing Value)
 * A fixed % applied to the remaining book value each year.
 * Assets lose more value early on.
 * Formula: Book Value = Cost × (1 - Rate)^Years
 *
 * @param {number} cost       - Original purchase price (KES)
 * @param {number} ratePercent - Annual depreciation rate (e.g. 25 = 25%)
 * @param {number} yearsUsed  - How many years the asset has been in use
 * @returns {object}          - Depreciation details
 */
export function reducingBalance(cost, ratePercent = 20, yearsUsed = 0) {
  if (!cost || cost <= 0) return emptyResult(cost);

  const rate       = ratePercent / 100;
  const bookValue  = cost * Math.pow(1 - rate, yearsUsed);
  const totalDep   = cost - bookValue;
  const pctDep     = (totalDep / cost) * 100;

  // Current year depreciation = last year BV × rate
  const lastYearBV = yearsUsed > 0 ? cost * Math.pow(1 - rate, yearsUsed - 1) : cost;
  const thisYearDep = lastYearBV * rate;

  return {
    method:         'Reducing Balance',
    cost,
    rate:           ratePercent,
    thisYearDep:    Math.round(thisYearDep),
    totalDep:       Math.round(totalDep),
    bookValue:      Math.round(Math.max(bookValue, 0)),
    salvageValue:   0,
    pctDepreciated: Math.round(Math.min(pctDep, 100)),
    yearsUsed:      Math.round(yearsUsed * 10) / 10,
    isFullyDep:     pctDep >= 99,
  };
}

function emptyResult(cost) {
  return { method:'None', cost:cost||0, bookValue:cost||0, totalDep:0, annualDep:0, pctDepreciated:0, yearsUsed:0, isFullyDep:false };
}

/**
 * Main entry point — calculates depreciation for an inventory item
 * Call this with any item object from the database
 */
export function calcDepreciation(item) {
  const cost     = Number(item.purchase_price) || 0;
  const years    = yearsElapsed(item.purchase_date);
  const method   = item.depreciation_method || 'straight_line';
  const quantity = Number(item.quantity) || 1;

  let result;
  if (method === 'straight_line') {
    result = straightLine(
      cost,
      Number(item.salvage_value) || 0,
      Number(item.useful_life_years) || 5,
      years
    );
  } else if (method === 'reducing_balance') {
    result = reducingBalance(
      cost,
      Number(item.depreciation_rate) || 20,
      years
    );
  } else {
    result = emptyResult(cost);
  }

  // Multiply by quantity for total asset value
  result.totalBookValue   = result.bookValue  * quantity;
  result.totalCost        = result.cost       * quantity;
  result.totalDepreciation= result.totalDep   * quantity;

  return result;
}

/**
 * Generate a year-by-year depreciation schedule (for the modal detail view)
 */
export function depreciationSchedule(item) {
  const cost   = Number(item.purchase_price) || 0;
  const method = item.depreciation_method || 'straight_line';
  const life   = Number(item.useful_life_years) || 5;
  const rate   = Number(item.depreciation_rate) || 20;
  const salvage= Number(item.salvage_value) || 0;

  if (!cost) return [];

  const schedule = [];
  const maxYears = method === 'reducing_balance' ? Math.min(life, 20) : life;

  for (let yr = 0; yr <= maxYears; yr++) {
    let bv, dep;
    if (method === 'straight_line') {
      const annual = (cost - salvage) / life;
      bv  = Math.max(cost - annual * yr, salvage);
      dep = yr === 0 ? 0 : Math.min(annual, cost - salvage);
    } else {
      bv  = cost * Math.pow(1 - rate / 100, yr);
      dep = yr === 0 ? 0 : cost * Math.pow(1 - rate / 100, yr - 1) * (rate / 100);
    }
    const purchaseYear = item.purchase_date ? new Date(item.purchase_date).getFullYear() : new Date().getFullYear();
    schedule.push({
      year:      purchaseYear + yr,
      yearNo:    yr,
      dep:       Math.round(dep),
      bookValue: Math.round(Math.max(bv, 0)),
      pct:       Math.round(Math.min(((cost - bv) / cost) * 100, 100)),
    });
  }
  return schedule;
}

/**
 * Returns the condition label based on % of value remaining
 * Useful for auto-updating condition field
 */
export function conditionFromBookValue(pctDepreciated) {
  if (pctDepreciated < 20)  return 'Excellent';
  if (pctDepreciated < 40)  return 'Good';
  if (pctDepreciated < 65)  return 'Fair';
  if (pctDepreciated < 85)  return 'Poor';
  return 'Needs Repair';
}

/**
 * Default depreciation settings per category name
 * Used to pre-fill the form when adding a new item
 */
export function defaultDepreciationForCategory(categoryName = '') {
  const name = categoryName.toLowerCase();

  if (name.includes('vehicle') || name.includes('car'))
    return { method:'reducing_balance', rate:25, life:8,  salvage:0 };
  if (name.includes('audio') || name.includes('visual') || name.includes('sound'))
    return { method:'reducing_balance', rate:33, life:3,  salvage:0 };
  if (name.includes('it') || name.includes('computer') || name.includes('admin'))
    return { method:'reducing_balance', rate:33, life:3,  salvage:0 };
  if (name.includes('facilities') || name.includes('building') || name.includes('cctv') || name.includes('security'))
    return { method:'reducing_balance', rate:25, life:10, salvage:0 };
  if (name.includes('furniture') || name.includes('seat') || name.includes('table') || name.includes('pew'))
    return { method:'straight_line',    rate:10, life:10, salvage:500 };
  if (name.includes('kitchen') || name.includes('appliance'))
    return { method:'straight_line',    rate:10, life:8,  salvage:500 };
  if (name.includes('musical') || name.includes('instrument'))
    return { method:'straight_line',    rate:10, life:10, salvage:1000 };
  if (name.includes('office'))
    return { method:'straight_line',    rate:10, life:5,  salvage:0 };
  if (name.includes('brigade') || name.includes('uniform') || name.includes('costume'))
    return { method:'straight_line',    rate:33, life:3,  salvage:0 };
  if (name.includes('tent') || name.includes('guild'))
    return { method:'straight_line',    rate:20, life:5,  salvage:0 };
  if (name.includes('church school') || name.includes('school'))
    return { method:'straight_line',    rate:20, life:5,  salvage:0 };

  // Default
  return { method:'straight_line', rate:20, life:5, salvage:0 };
}

// ── Formatting helpers ────────────────────────────────────────
export function fmtKES(n) {
  return 'KES ' + Math.round(n || 0).toLocaleString();
}

export function bookValueColor(pctDepreciated) {
  if (pctDepreciated < 25)  return { color:'#1A7A32', bg:'#E8F5E9' }; // green
  if (pctDepreciated < 50)  return { color:'#2E7D32', bg:'#E8F5E9' }; // green
  if (pctDepreciated < 70)  return { color:'#C47A00', bg:'#FFF8E0' }; // amber
  if (pctDepreciated < 90)  return { color:'#CC0000', bg:'#FFEBEE' }; // red
  return                           { color:'#546E7A', bg:'#ECEFF1' }; // grey — fully dep
}
